import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import moment from 'moment';

import { IRecarga } from './interfaces';

import { validacaoPermissao, VerificaRota } from '../../../../services/VerificaSePodeAcessar';
import { EmitirErro } from '../../../../services/EmitirErro';
import { SairDoSistema } from '../../../../services/LStorage/SairSistema';
import { useGetColumnSearchProps } from '../../../../hooks/FilterTable/string';
import RecargasService from '../../../../services/http/services/CongelamentoPorCNPJ/RecargasService';
import { convertDataUSParaBrasilEHora, convertMoedaBRDoJSComExtamente2CasasDecimais, convertMoedaBRDoJSComAte3CasasDecimais } from '../../../../services/Conversores';

export const useRecargas = () => {

    const navigate = useNavigate();

    const refContagemDoFiltro = React.useRef<Array<any>>([]);

    const refAbortController = useRef<AbortController>();

    const [dadosTabelaState, setDadosTabelaState] = useState<IRecarga[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<string | number>>([]);
    const [hasSelectedEdit, setHasSelectedEdit] = useState(false);

    const [loadingScreen, setLoadingScreen] = useState(false);

    useEffect(() => () => {
        refAbortController.current?.abort();
    }, []);

    const reqbuscarDadosTable = useCallback(() => {

        const requestAsync = async () => {
            try {
                setLoadingScreen(true);

                const response = await RecargasService.get(refAbortController.current?.signal);

                setDadosTabelaState(response);

                setLoadingScreen(false);

            } catch (error) {
                setLoadingScreen(false);
                let msgErro: any = (error as Error);

                if (msgErro?.message === 'USUáRIO NãO AUTORIZADO.') {
                    SairDoSistema();
                    notification.error({
                        message: 'Sessão expirada',
                        description:
                            'Sua sessão expirou, realize o login novamente!',
                    });
                    navigate('/');
                } else {
                    EmitirErro(error, true, 'congelamentoCErtidoes_dkwdfaiweyi')
                }
            }
        }

        requestAsync();

    }, []);

    useEffect(() => {
        refAbortController.current = new AbortController();

        let arrayUrl = window.location.pathname.split("/");
        VerificaRota(arrayUrl, navigate, true, validacaoPermissao(['manage_config_congelamento_cnpj']));

        reqbuscarDadosTable();


    }, []);

    const verifyIfCanEditSelectedItem = useCallback((selectedId: string | number) => {
        const selectedItem = dadosTabelaState.find((e) => e.id === selectedId);

        const foundNewerRecarga = !!dadosTabelaState.find((e) => {
            if (e.id === selectedId) {
                return false;
            }

            let aMonMen: any = null
            let bMonMen: any = null
            if (selectedItem?.createdAt) {
                aMonMen = moment(selectedItem.createdAt).unix()
            } else {
                aMonMen = 0
            }
            if (e.createdAt) {
                bMonMen = moment(e.createdAt).unix()
            } else {
                bMonMen = 0
            }

            return bMonMen > aMonMen;
        });

        return !foundNewerRecarga;
    }, [dadosTabelaState]);

    const onclickTable = useCallback((record: any, row: any): void => {
        if (selectedRowKeys.includes(record.id)) {
            let filter = selectedRowKeys.filter((val) => {
                return val != record.id
            })
            if (filter.length === 1) {
                setSelectedRowKeys(filter);

                const selectedId = filter[0];

                const canEdit = verifyIfCanEditSelectedItem(selectedId);

                if (canEdit) {
                    setHasSelectedEdit(true);
                }
            } else if (filter.length === 0) {
                setSelectedRowKeys(filter);
                setHasSelectedEdit(false);
            } else {
                setSelectedRowKeys(filter);
            }

        } else {
            if (selectedRowKeys.length == 0) {
                setSelectedRowKeys([record.id]);

                const canEdit = verifyIfCanEditSelectedItem(record.id);

                if (canEdit) {
                    setHasSelectedEdit(true);
                }
            } else {
                let teste = selectedRowKeys.concat(record.id);
                setSelectedRowKeys(teste);
                setHasSelectedEdit(false);
            }
        }
    }, [selectedRowKeys, verifyIfCanEditSelectedItem]);

    const onSelectChange = useCallback((selectedRowKeys: Array<string | number>, selectedRows: Array<any>): void => {
        if (selectedRowKeys.length > 1) {
            setSelectedRowKeys(selectedRowKeys);
            setHasSelectedEdit(false);
        } else if (selectedRowKeys.length === 0) {
            setSelectedRowKeys(selectedRowKeys);
            setHasSelectedEdit(false);
        } else {
            setSelectedRowKeys(selectedRowKeys);
            const selectedId = selectedRowKeys[0];

            const canEdit = verifyIfCanEditSelectedItem(selectedId);

            if (canEdit) {
                setHasSelectedEdit(true);
            }
        }
    }, [selectedRowKeys, verifyIfCanEditSelectedItem]);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: '1%'
    };

    const columns = [
        {
            title: 'Valor',
            dataIndex: 'valor',
            ...useGetColumnSearchProps('valor'),
            width: '8%',
            sorter: (a: IRecarga, b: IRecarga) => a.valor - b.valor,
            showSorterTooltip: false,
            render: (valor: number) => {
                return `R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(valor, true)}`;
            }
        },
        {
            title: 'Saldo restante',
            dataIndex: 'saldoRestante',
            ...useGetColumnSearchProps('saldoRestante'),
            width: '6%',
            sorter: (a: IRecarga, b: IRecarga) => a.saldoRestante - b.saldoRestante,
            showSorterTooltip: false,
            render: (valor: number) => {
                return `R$ ${convertMoedaBRDoJSComAte3CasasDecimais(valor, true)}`;
            }
        },
        {
            title: 'Criado em',
            dataIndex: 'createdAt',
            ...useGetColumnSearchProps('createdAt'),
            width: '2%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.createdAt) {
                    aMonMen = moment(a.createdAt).unix()
                } else {
                    aMonMen = 0
                }
                if (b.createdAt) {
                    bMonMen = moment(b.createdAt).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (data: any) => convertDataUSParaBrasilEHora(data)
        },
        {
            title: 'Atualizado em',
            dataIndex: 'updatedAt',
            ...useGetColumnSearchProps('updatedAt'),
            width: '2%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.createdAt) {
                    aMonMen = moment(a.createdAt).unix()
                } else {
                    aMonMen = 0
                }
                if (b.createdAt) {
                    bMonMen = moment(b.createdAt).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (data: any) => convertDataUSParaBrasilEHora(data)
        },
    ];

    const clickBotaoEditar = useCallback((): void => {
        if (selectedRowKeys.length == 1) {

            let objSelecionado: any = {}
            dadosTabelaState.forEach((dados): void => {
                if (dados.id === selectedRowKeys[0]) {
                    objSelecionado = dados
                }
            })

            localStorage.setItem('@DesenvolveSP:configuracoesEditar', JSON.stringify(objSelecionado));

            navigate(`/congelamento-cnpj/recargas/${selectedRowKeys[0]}`)
        }
    }, [selectedRowKeys, dadosTabelaState]);

    return {
        refContagemDoFiltro,
        columns,
        dadosTabelaState,
        navigate,
        clickBotaoEditar,
        hasSelectedEdit,
        loadingScreen,
        onclickTable,
        rowSelection,
    }
}
